import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import {
  NiveauBadge,
  MatiereBadge,
  ResourceTypeBadge,
  StatusBadge,
  formatRelativeTime,
} from "./constants";
import {
  ExternalLink,
  Edit,
  Copy,
  Calendar,
  Share2,
  MoreHorizontal,
  Clock,
  FileText,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

function ResourceCard({ resource, onDuplicate, onSchedule, onShare }) {
  const navigate = useNavigate();

  const handleOpen = () => {
    if (resource.resource_type === 'fiche' || resource.elements) {
      // Visual document - open in visual editor
      navigate(`/create/${resource.id}`);
    } else if (resource.raw_text) {
      // Text document - open in text editor
      navigate(`/editor/${resource.id}`);
    }
  };

  const handleEdit = () => {
    if (resource.resource_type === 'fiche' || resource.elements) {
      navigate(`/create/${resource.id}`);
    } else {
      navigate(`/editor/${resource.id}`);
    }
  };

  return (
    <Card className="group hover:shadow-md transition-all duration-200 border-slate-200 hover:border-indigo-200 overflow-hidden">
      <CardContent className="p-0">
        {/* Thumbnail or Placeholder */}
        <div 
          className="h-28 sm:h-32 bg-gradient-to-br from-slate-50 to-slate-100 relative cursor-pointer"
          onClick={handleOpen}
        >
          {resource.thumbnail ? (
            <img
              src={resource.thumbnail}
              alt={resource.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FileText className="w-10 h-10 text-slate-300" />
            </div>
          )}
          
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-indigo-600/0 group-hover:bg-indigo-600/10 transition-colors flex items-center justify-center">
            <ExternalLink className="w-6 h-6 text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          
          {/* Status badge */}
          <div className="absolute top-2 right-2">
            <StatusBadge status={resource.status} />
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          <h3 
            className="font-semibold text-slate-800 text-sm line-clamp-1 cursor-pointer hover:text-indigo-600 mb-2"
            onClick={handleOpen}
          >
            {resource.title}
          </h3>

          {/* Metadata badges */}
          <div className="flex flex-wrap gap-1 mb-3">
            {resource.niveau && <NiveauBadge niveau={resource.niveau} className="text-xs" />}
            {resource.matiere && <MatiereBadge matiere={resource.matiere} className="text-xs" />}
            {resource.resource_type && <ResourceTypeBadge type={resource.resource_type} className="text-xs" />}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatRelativeTime(resource.updated_at)}
            </span>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={handleEdit}
                title="Modifier"
              >
                <Edit className="w-3.5 h-3.5" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onDuplicate?.(resource)}>
                    <Copy className="w-4 h-4 mr-2" />
                    Dupliquer
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSchedule?.(resource)}>
                    <Calendar className="w-4 h-4 mr-2" />
                    Planifier
                  </DropdownMenuItem>
                  {!resource.is_shared && (
                    <DropdownMenuItem onClick={() => onShare?.(resource)}>
                      <Share2 className="w-4 h-4 mr-2" />
                      Partager
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ResourceCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <Skeleton className="h-28 sm:h-32 w-full" />
        <div className="p-3">
          <Skeleton className="h-4 w-3/4 mb-2" />
          <div className="flex gap-1 mb-3">
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-16" />
          </div>
          <div className="flex justify-between">
            <Skeleton className="h-3 w-20" />
            <div className="flex gap-1">
              <Skeleton className="h-7 w-7" />
              <Skeleton className="h-7 w-7" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function RecentWorksPanel({ resources, loading, onDuplicate, onSchedule, onShare }) {
  const navigate = useNavigate();

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-500" />
          Derniers travaux
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/create")}
          className="text-indigo-600 hover:text-indigo-700"
        >
          Voir tout
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {[...Array(5)].map((_, i) => (
            <ResourceCardSkeleton key={i} />
          ))}
        </div>
      ) : resources.length === 0 ? (
        <Card className="bg-slate-50 border-dashed">
          <CardContent className="py-8 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-3">Aucune ressource pour le moment</p>
            <Button onClick={() => navigate("/create")} className="bg-indigo-600 hover:bg-indigo-700">
              Créer ma première ressource
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {resources.map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              onDuplicate={onDuplicate}
              onSchedule={onSchedule}
              onShare={onShare}
            />
          ))}
        </div>
      )}
    </div>
  );
}
